/**
 * Graficos — widgets de rosca do dashboard original: Resultados de
 * Auditoria (conforme × apontamentos) e Status dos Processos
 * (apenas DFD × DFD+ETP × completos), com legenda em pílulas.
 */

import { faChartPie, faSearchDollar } from '@fortawesome/free-solid-svg-icons'
import { useQuery } from '@tanstack/react-query'
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import { resumoDashboard } from '../../../api/dashboard'
import { CardWidget } from './CardWidget'

ChartJS.register(ArcElement, Tooltip, Legend)

const OPCOES = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '65%',
  plugins: { legend: { display: false } },
} as const

function Legenda({ itens }: { itens: { cor: string; rotulo: string; valor: number }[] }) {
  return (
    <div className="mt-4 flex flex-wrap justify-center gap-3">
      {itens.map((i) => (
        <span
          key={i.rotulo}
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-3 py-1 text-xs text-slate-600 dark:bg-dark-hover dark:text-slate-300"
        >
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: i.cor }} />
          {i.rotulo} <strong className="text-slate-800 dark:text-white">({i.valor})</strong>
        </span>
      ))}
    </div>
  )
}

function Vazio({ mensagem }: { mensagem: string }) {
  return (
    <div className="flex h-48 items-center justify-center px-4 text-center text-sm text-slate-500">
      {mensagem}
    </div>
  )
}

export function ResultadosAuditoria() {
  const { data: resumo } = useQuery({
    queryKey: ['dashboard-resumo'],
    queryFn: resumoDashboard,
  })
  const conforme = resumo?.auditorias.conforme ?? 0
  const apontamentos = resumo?.auditorias.com_apontamentos ?? 0
  const total = conforme + apontamentos

  return (
    <CardWidget
      titulo="Resultados de Auditoria"
      subtitulo="Conformidade vs Apontamentos"
      icone={faSearchDollar}
      gradiente="from-emerald-600 to-teal-600"
      corSubtitulo="text-emerald-100"
    >
      <div className="p-4">
        {total > 0 ? (
          <>
            <div className="h-48">
              <Doughnut
                data={{
                  labels: ['Conforme', 'Com Apontamentos'],
                  datasets: [
                    {
                      data: [conforme, apontamentos],
                      backgroundColor: ['#10b981', '#f43f5e'],
                      borderColor: '#fff',
                      borderWidth: 4,
                      hoverOffset: 8,
                    },
                  ],
                }}
                options={OPCOES}
              />
            </div>
            <Legenda
              itens={[
                { cor: '#10b981', rotulo: 'Conforme', valor: conforme },
                { cor: '#f43f5e', rotulo: 'Com Apontamentos', valor: apontamentos },
              ]}
            />
          </>
        ) : (
          <Vazio mensagem="Nenhuma auditoria concluída ainda." />
        )}
      </div>
    </CardWidget>
  )
}

export function StatusProcessos() {
  const { data: resumo } = useQuery({
    queryKey: ['dashboard-resumo'],
    queryFn: resumoDashboard,
  })
  const p = resumo?.processos
  // etapas EXCLUSIVAS (o gráfico legado somava faixas cumulativas e a fatia
  // "Completos" saía undefined)
  const apenasDfd = Math.max((p?.com_dfd ?? 0) - (p?.com_dfd_etp ?? 0), 0)
  const dfdEtp = Math.max((p?.com_dfd_etp ?? 0) - (p?.com_edital ?? 0), 0)
  const completos = p?.com_edital ?? 0
  const total = apenasDfd + dfdEtp + completos

  return (
    <CardWidget
      titulo="Status dos Processos"
      subtitulo="Distribuição por etapa"
      icone={faChartPie}
      gradiente="from-violet-600 to-purple-600"
      corSubtitulo="text-violet-100"
    >
      <div className="p-4">
        {total > 0 ? (
          <>
            <div className="h-48">
              <Doughnut
                data={{
                  labels: ['Apenas DFD', 'DFD + ETP', 'Completos'],
                  datasets: [
                    {
                      data: [apenasDfd, dfdEtp, completos],
                      backgroundColor: ['#3b82f6', '#f59e0b', '#10b981'],
                      borderColor: '#fff',
                      borderWidth: 4,
                      hoverOffset: 8,
                    },
                  ],
                }}
                options={OPCOES}
              />
            </div>
            <Legenda
              itens={[
                { cor: '#3b82f6', rotulo: 'Apenas DFD', valor: apenasDfd },
                { cor: '#f59e0b', rotulo: 'DFD + ETP', valor: dfdEtp },
                { cor: '#10b981', rotulo: 'Completos', valor: completos },
              ]}
            />
          </>
        ) : (
          <Vazio mensagem="Nenhum processo com documentos gerados." />
        )}
      </div>
    </CardWidget>
  )
}
